<template>
  <Modal ref="modalRef" @after-close="clearForm" @before-show="populateForm">
    <template #body>
      <View gap="xl" direction="column" class="modal-body">
        <View gap="md" direction="column" align="center" class="header">
          <Text variant="h2" align="center">{{ acceptCopy.title }}</Text>

          <Text variant="body-large" align="center" color="secondary">
            {{ acceptCopy.subtitle }}
          </Text>
        </View>

        <FormField :label="agentCopy.fieldLabel" :error="form.fieldErrors('showingAgentId')">
          <ShowingAgentPicker v-model="form.fields.showingAgentId" :property-id="propertyId" />

          <!--
            A sibling Text rather than FormField's `hint`, which is hidden while the field has an
            error — exactly when a self-guided owner most needs to know why an agent is asked for.
          -->
          <Text v-if="agentCopy.fieldHint" variant="body-small" color="secondary">
            {{ agentCopy.fieldHint }}
          </Text>
        </FormField>

        <Card size="medium" variant="outline">
          <View gap="md" direction="column">
            <Text variant="h4">What happens next?</Text>

            <View v-for="(step, index) in acceptCopy.steps" :key="step.title" gap="md" direction="row">
              <View align="center" justify="center" class="item-number">{{ index + 1 }}</View>
              <View gap="xs" direction="column">
                <Text variant="h4">{{ step.title }}</Text>
                <Text variant="body-medium" color="secondary">
                  {{ step.description }}
                </Text>
              </View>
            </View>
          </View>
        </Card>
      </View>
    </template>

    <template #footer>
      <View gap="md" :grow="1" class="actions">
        <Button variant="secondary" @click="close">Cancel</Button>
        <Button :loading="isLoading" @click="accept">Accept</Button>
      </View>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { helpers, required } from '@vuelidate/validators';
import { computed, ref } from 'vue';

import { Button, Card, FormField, Modal, Text, View, useAlertsStore, useForm } from '@tallo/design-system';
import { ShowingAgentPicker } from '@tallo/investor/showing-agents';

import { TourType } from '../../interfaces/tour-type.enum';
import { useAcceptShowingRequest } from '../../mutations/accept-showing-request.mutation';
import { getAcceptShowingCopy, getShowingAgentCopy } from '../../utils/showing-agent-copy.util';

const props = defineProps<{
  renterName: string;
  propertyId: string;
  showingRequestId: string;
  tourType: TourType;
  showingAgentId?: string;
}>();

const { isLoading, mutateAsync: acceptShowing } = useAcceptShowingRequest();
const alertsStore = useAlertsStore();

const modalRef = ref<InstanceType<typeof Modal> | null>(null);
const name = computed(() => props.renterName.split(' ')[0]);
const acceptCopy = computed(() => getAcceptShowingCopy(props.tourType, name.value));
const agentCopy = computed(() => getShowingAgentCopy(props.tourType));

const form = useForm<{ showingAgentId: string | null }>(
  {
    showingAgentId: null,
  },
  {
    showingAgentId: computed(() => ({
      required: helpers.withMessage(agentCopy.value.fieldRequiredError, required),
    })),
  },
);

function open() {
  modalRef.value?.show();
}

function close() {
  modalRef.value?.close();
}

function populateForm() {
  form.fields.showingAgentId = props.showingAgentId as string;
}

function clearForm() {
  form.fields.showingAgentId = '';
  form.resetValidation();
}

function accept() {
  form.submit(async () => {
    await acceptShowing({
      propertyId: props.propertyId,
      showingRequestId: props.showingRequestId,
      showingAgentId: form.fields.showingAgentId as string,
    });

    close();
    alertsStore.success('Showing accepted');
  });
}

defineExpose({ open });
</script>

<style scoped lang="scss">
.header {
  max-width: 30rem;
  margin: auto;
}

.modal-body {
  max-width: 37rem;
}

.item-number {
  width: 2.25rem;
  height: 2.25rem;
  flex-shrink: 0;
  background-color: var(--t-color-neutral-200);
  border-radius: var(--t-border-radius-sm);
}

.actions > * {
  width: 100%;
}
</style>
